// ABOUTME: Upgrade page for users to compare plans and initiate Stripe checkout.
// ABOUTME: Fetches plan data from the API and handles Stripe success/cancelled states.

import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Zap, AlertCircle, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { billingApi } from '@/services/api/billingClient';
import { formatTierName } from '@/utils/billing';
import type { UsageSummary } from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface PublicPlan {
  name: string;
  display_name: string;
  description: string;
  is_self_service: boolean;
  price_monthly_cents: number;
  sort_order: number;
  limits: {
    max_users: number;
    max_data_sources: number;
    max_reports_per_month: number;
    max_messages_per_month: number;
    max_active_investigations: number;
  };
}

/** Format a limit value for display. -1 means unlimited. */
function formatLimitValue(value: number): string {
  if (value === -1) return 'Unlimited';
  return String(value);
}

export function UpgradePage() {
  const [searchParams] = useSearchParams();
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const upgradeStatus = searchParams.get('upgrade');
  const isSuccess = upgradeStatus === 'success';
  const isCancelled = upgradeStatus === 'cancelled';

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usageData, plansData] = await Promise.all([
        billingApi.getUsage(),
        fetch(`${API_BASE_URL}/plans`).then(r => {
          if (!r.ok) throw new Error('Failed to load plans');
          return r.json() as Promise<PublicPlan[]>;
        }),
      ]);
      setUsage(usageData);
      setPlans(plansData);
    } catch (err) {
      console.error('Failed to load upgrade data:', err);
      setError('Unable to load your subscription information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpgrade = async () => {
    setIsCheckoutLoading(true);
    try {
      const successUrl = `${window.location.origin}/upgrade?upgrade=success`;
      const cancelUrl = `${window.location.origin}/upgrade?upgrade=cancelled`;
      const { checkout_url } = await billingApi.createCheckoutSession(successUrl, cancelUrl);
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError('Unable to start checkout. Please try again.');
      setIsCheckoutLoading(false);
    }
  };

  // Find the user's current plan and the upgrade target from the API data
  const currentPlan = plans.find(p => p.name === usage?.subscription_tier);
  const upgradePlan = plans.find(p => p.is_self_service && p.price_monthly_cents > 0);

  // Build comparison rows from plan data
  const comparisonRows = currentPlan && upgradePlan ? [
    { name: 'Messages', current: `${formatLimitValue(currentPlan.limits.max_messages_per_month)} messages/mo`, upgrade: `${formatLimitValue(upgradePlan.limits.max_messages_per_month)} messages/mo`, highlight: true },
    { name: 'Investigations', current: `${formatLimitValue(currentPlan.limits.max_active_investigations)} active`, upgrade: `${formatLimitValue(upgradePlan.limits.max_active_investigations)} active` },
    { name: 'Data Sources', current: `${formatLimitValue(currentPlan.limits.max_data_sources)} sources`, upgrade: `${formatLimitValue(upgradePlan.limits.max_data_sources)} sources` },
    { name: 'Reports', current: `${formatLimitValue(currentPlan.limits.max_reports_per_month)} reports/mo`, upgrade: `${formatLimitValue(upgradePlan.limits.max_reports_per_month)} reports/mo` },
  ] : [];

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex items-center gap-3 text-surface-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading upgrade options...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !usage) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <AlertCircle className="w-12 h-12 text-error" />
            <p className="text-surface-600">{error}</p>
            <Button onClick={loadData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <Card variant="glass" className="text-center">
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-surface-900 mb-2">
                  Upgrade Successful!
                </h1>
                <p className="text-surface-600">
                  Thank you for upgrading{upgradePlan ? ` to ${upgradePlan.display_name}` : ''}. Your account has been upgraded
                  and you now have access to all premium features.
                </p>
              </div>
              <Link
                to="/"
                className="btn btn-primary btn-md"
              >
                Go to Dashboard
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Cancelled state
  if (isCancelled) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-warning/10 text-warning border border-warning/20 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Upgrade not completed. You can try again when you're ready.</span>
            </div>
          </div>

          {renderUpgradeContent()}
        </div>
      </div>
    );
  }

  function renderUpgradeContent() {
    const isFreeTier = usage?.subscription_tier === 'freemium';

    return (
      <>
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/settings/billing"
            className="inline-flex items-center gap-2 text-surface-600 hover:text-surface-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Billing
          </Link>
          <h1 className="text-3xl font-bold text-surface-900 mb-2">
            Upgrade Your Plan
          </h1>
          <p className="text-surface-600">
            Get more out of Oleum BI with additional features and higher limits.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
            {error}
          </div>
        )}

        {/* Current Usage Summary */}
        {usage && isFreeTier && (
          <Card variant="surface" className="mb-8 bg-surface-50">
            <h2 className="text-lg font-semibold text-surface-900 mb-4">
              Your Current Usage
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-surface-500">Messages</div>
                <div className="text-lg font-semibold text-surface-900">
                  {usage.usage.messages.used} / {usage.usage.messages.limit === -1 ? '∞' : usage.usage.messages.limit}
                </div>
              </div>
              <div>
                <div className="text-sm text-surface-500">Investigations</div>
                <div className="text-lg font-semibold text-surface-900">
                  {usage.usage.active_investigations.used} / {usage.usage.active_investigations.limit === -1 ? '∞' : usage.usage.active_investigations.limit}
                </div>
              </div>
              <div>
                <div className="text-sm text-surface-500">Data Sources</div>
                <div className="text-lg font-semibold text-surface-900">
                  {usage.usage.data_sources.used} / {usage.usage.data_sources.limit === -1 ? '∞' : usage.usage.data_sources.limit}
                </div>
              </div>
              <div>
                <div className="text-sm text-surface-500">Reports</div>
                <div className="text-lg font-semibold text-surface-900">
                  {usage.usage.reports_generated.used} / {usage.usage.reports_generated.limit === -1 ? '∞' : usage.usage.reports_generated.limit}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Plan Comparison */}
        {currentPlan && upgradePlan ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Current Plan */}
            <Card variant="glass">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-medium text-surface-500 uppercase tracking-wide">
                  Current Plan
                </span>
              </div>
              <h3 className="text-2xl font-bold text-surface-900 mb-1">
                {currentPlan.display_name}
              </h3>
              <p className="text-surface-500 mb-6">
                {currentPlan.price_monthly_cents === 0 ? '$0/month' : `$${(currentPlan.price_monthly_cents / 100).toFixed(0)}/month`}
              </p>

              <ul className="space-y-3">
                {comparisonRows.map((row) => (
                  <li key={row.name} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-surface-400" />
                    <span className="text-surface-600">{row.current}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Target Plan */}
            <Card variant="surface" className="ring-2 ring-primary-500 relative" overflowVisible>
              <div className="absolute -top-3 left-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-full">
                  <Zap className="w-3 h-3" />
                  Recommended
                </span>
              </div>
              <div className="flex items-center gap-2 mb-4 mt-2">
                <span className="text-sm font-medium text-primary-600 uppercase tracking-wide">
                  Upgrade To
                </span>
              </div>
              <h3 className="text-2xl font-bold text-surface-900 mb-1">
                {upgradePlan.display_name}
              </h3>
              <p className="text-surface-900 mb-6">
                <span className="text-3xl font-bold">${(upgradePlan.price_monthly_cents / 100).toFixed(0)}</span>
                <span className="text-surface-500">/month</span>
              </p>

              <ul className="space-y-3 mb-6">
                {comparisonRows.map((row) => (
                  <li
                    key={row.name}
                    className={`flex items-center gap-2 text-sm ${
                      row.highlight ? 'font-medium' : ''
                    }`}
                  >
                    <Check className="w-4 h-4 text-success" />
                    <span className={row.highlight ? 'text-surface-900' : 'text-surface-600'}>
                      {row.upgrade}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                variant="primary"
                className="w-full"
                onClick={handleUpgrade}
                isLoading={isCheckoutLoading}
              >
                Upgrade Now
              </Button>
            </Card>
          </div>
        ) : (
          <div className="text-center text-surface-500 py-12">
            {usage ? `You're on the ${formatTierName(usage.subscription_tier)} plan.` : 'Loading plan information...'}
          </div>
        )}

        {/* FAQ */}
        <Card variant="glass">
          <h3 className="text-lg font-semibold text-surface-900 mb-4">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-surface-900 mb-1">
                Can I cancel anytime?
              </h4>
              <p className="text-surface-600">
                Yes, you can cancel your subscription at any time. Your access will continue
                until the end of your billing period.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-surface-900 mb-1">
                What payment methods do you accept?
              </h4>
              <p className="text-surface-600">
                We accept all major credit cards through Stripe, our secure payment processor.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-surface-900 mb-1">
                Need more than {upgradePlan?.display_name || 'the paid plan'}?
              </h4>
              <p className="text-surface-600">
                Contact us at{' '}
                <a href="mailto:sales@oleum.pro" className="text-primary-600 hover:text-primary-700">
                  sales@oleum.pro
                </a>{' '}
                for Enterprise pricing.
              </p>
            </div>
          </div>
        </Card>
      </>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {renderUpgradeContent()}
      </div>
    </div>
  );
}
